import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const RecipientField = ({ label, value, onChange, token, autoFocus, testId, right }) => {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [hi, setHi] = useState(0);
  const [focused, setFocused] = useState(false);
  const timer = useRef();

  useEffect(() => {
    clearTimeout(timer.current);
    if (!focused || !input.trim()) return setSuggestions([]);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/admin/emails/recipients`, { params: { q: input }, headers: { Authorization: `Bearer ${token}` } });
        setSuggestions(data.filter((s) => !value.includes(s.email)));
        setHi(0);
      } catch { setSuggestions([]); }
    }, 150);
    return () => clearTimeout(timer.current);
  }, [input, focused, token, value]);

  const add = (email) => {
    const e = (email || '').trim().replace(/[,;]$/, '');
    if (!e) return;
    if (!EMAIL_RX.test(e)) return;
    if (!value.includes(e)) onChange([...value, e]);
    setInput('');
  };
  const onKey = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',' || e.key === ';') {
      if (suggestions[hi] && input) { e.preventDefault(); add(suggestions[hi].email); return; }
      if (input.trim()) { e.preventDefault(); add(input); }
    } else if (e.key === 'Backspace' && !input && value.length) onChange(value.slice(0, -1));
    else if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Escape') setFocused(false);
  };

  return (
    <div className="relative flex items-start gap-2 border-b border-neutral-200 py-1.5" data-testid={testId}>
      <span className="text-xs text-neutral-500 w-10 pt-1.5 shrink-0">{label}</span>
      <div className="flex-1 flex flex-wrap items-center gap-1 min-h-[30px]">
        {value.map((e) => (
          <span key={e} className="inline-flex items-center gap-1 rounded-full bg-neutral-100 border border-neutral-200 pl-2 pr-1 py-0.5 text-xs text-neutral-800" data-testid={`${testId}-chip`}>
            {e}
            <button type="button" onClick={() => onChange(value.filter((x) => x !== e))} className="rounded-full p-0.5 hover:bg-neutral-300" aria-label={`Remove ${e}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
        <input value={input} autoFocus={autoFocus} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey}
          onFocus={() => setFocused(true)} onBlur={() => { setTimeout(() => setFocused(false), 150); if (input.trim()) add(input); }}
          placeholder={value.length ? '' : 'Type a name or email…'} className="flex-1 min-w-[140px] h-7 bg-transparent outline-none text-sm" data-testid={`${testId}-input`} />
      </div>
      {right}
      {focused && suggestions.length > 0 && (
        <ul className="absolute left-12 right-0 top-full z-50 mt-1 max-h-56 overflow-y-auto rounded-lg border border-neutral-200 bg-white shadow-xl py-1" data-testid={`${testId}-suggestions`}>
          {suggestions.map((s, i) => (
            <li key={s.email}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); add(s.email); }} className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${i === hi ? 'bg-amber-50' : 'hover:bg-neutral-50'}`} data-testid={`${testId}-suggestion`}>
                <span className="font-medium text-neutral-900 truncate">{s.name || s.email}</span>
                {s.name && <span className="text-neutral-500 truncate">{s.email}</span>}
                <span className="ml-auto text-[10px] text-neutral-400">{s.source}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default RecipientField;
